import { Settings } from "@mui/icons-material";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import { Box, IconButton, InputLabel, MenuItem, Popover, Select, Skeleton } from "@mui/material";
import React, { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import Webcam from "react-webcam";

import { FlipCameraIos } from "assets/icons";
import { GroundlightButton } from "new-components/buttons/GroundlightButton";
import { Typography } from "new-components/typography/Typography";
import { WebcamRef } from "pages/demo/DemoPage";

interface WebcamProps {
  height?: string | number;
  deviceId?: string;
  setDeviceId: (deviceId: string) => void;
}

// Creates a Wecam component with a settings button in the lower left corner.
// The settings allow choosing which camera and which direction the camera is facing.
export const SimpleWebcam = forwardRef<WebcamRef, WebcamProps>((props: WebcamProps, ref: ForwardedRef<WebcamRef>) => {
  const { height = 350, deviceId, setDeviceId } = props;
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [facingUser, setFacingUser] = useState<boolean>(true);
  const webcamRef = useRef<Webcam>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  useImperativeHandle(ref, () => ({
    getScreenshot(): string | null {
      if (webcamRef && webcamRef.current) {
        return webcamRef.current.getScreenshot();
      }
      return null;
    },
  }));

  const openPopover = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const closePopover = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const setDeviceIdCallback = useCallback(
    (id: string) => {
      if (deviceId === undefined) {
        setDeviceId(id);
      }
    },
    [deviceId, setDeviceId]
  );

  // Adapted from https://github.com/mozmorris/react-webcam#show-all-cameras-by-deviceid
  const filterDevices = useCallback(
    (mediaDevices: MediaDeviceInfo[]) => {
      const filtered = mediaDevices.filter(({ kind }) => kind === "videoinput");
      setDevices(filtered);
      if (filtered.length > 0) {
        setDeviceIdCallback(filtered[0].deviceId);
      }
    },
    [setDeviceIdCallback]
  );

  useEffect(() => {
    // Function to handle device enumeration
    const handleDevices = async () => {
      try {
        // Preliminary getUserMedia call to prompt for permissions
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach((track) => track.stop()); // Stop using the stream

        // Enumerate devices after permissions are granted
        const devices = await navigator.mediaDevices.enumerateDevices();
        filterDevices(devices);
      } catch (error) {
        console.error("Error accessing media devices:", error);
      }
    };

    handleDevices();
  }, [filterDevices]);

  function changeDevice(id: string) {
    setDeviceId(id);
    closePopover();
  }

  return deviceId !== undefined ? (
    <Box>
      <Box>
        <Webcam
          audio={false}
          height={height}
          style={{ maxHeight: `${height}px` }}
          screenshotFormat="image/jpeg"
          videoConstraints={{
            deviceId,
            facingMode: facingUser ? "user" : { exact: "environment" },
          }}
          ref={webcamRef}
        />
      </Box>
      <Box sx={{ mt: -1 }}>
        <IconButton
          color="primary"
          aria-label="switch direction"
          component="span"
          onClick={openPopover}
          sx={{ mt: -8 }}
        >
          <Settings />
        </IconButton>
        <Popover
          sx={{ pt: 4 }}
          open={open}
          anchorEl={anchorEl}
          onClose={closePopover}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box sx={{ m: "10px" }}>
            <GroundlightButton
              fullWidth
              startIcon={FlipCameraIos}
              variant="border"
              aria-label="switch direction"
              onClick={() => setFacingUser(!facingUser)}
              sx={{
                width: "380px", mb: "10px"
              }}
            >
              <Typography>Switch Direction</Typography>
            </GroundlightButton>

            <InputLabel id="camera-label">Camera</InputLabel>
            <Select
              labelId="camera-label"
              fullWidth
              value={deviceId}
              onChange={(e) => {
                changeDevice(e.target.value);
              }}
            >
              {devices?.map((device) => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Popover>
      </Box>
    </Box>
  ) : (
    <div style={{ position: "relative", height }} className="w-full">
      <Skeleton
        animation={false}
        variant="rectangular"
        sx={{ background: "#E5E9ED", borderRadius: "15px" }}
        height={height}
      />
      <NoPhotographyIcon
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          background: "#C8CDD3",
          fontSize: "4rem",
          borderRadius: "50%",
          padding: "0.5rem",
          color: "#E5E9ED",
        }}
      />
    </div>
  );
});
